import { Box } from '@chakra-ui/react';
import { useLingui } from '@lingui/react/macro';
import { useRef } from 'react';

import { Notice, ServerOffIcon, WifiOffIcon } from '#features/ui';
import { useOfflineBanner, type ConnectionTrouble } from '../hooks/useOfflineBanner';

/**
 * Полоса "Нет подключения к интернету. Ждём сеть" под шапкой. Когда сеть вроде бы есть, а сервер
 * не отвечает (см. `markNetworkFailure`), та же полоса говорит "Нет связи с сервером. Пробуем
 * снова": человек видит, что данные на экране могут быть старыми, и не ищет беду у себя.
 *
 * Стоит внутри шапки, в потоке, поэтому не накрывает страницу, а сдвигает её вниз и едет вместе
 * с липкой шапкой; безопасный отступ сверху шапка уже учла. Появляется не сразу (см.
 * `useOfflineBanner`) и уходит сама, как только сеть вернулась. Пока она на экране, тосты об
 * ошибках чтения молчат (`queryClient.ts`): причину человек уже видит.
 *
 * Разворачивается сменой `grid-template-rows` с `0fr` на `1fr`, как аккордеоны. Читалке полоса
 * не видна, вместо неё объявляется скрытая строка в живой области: так фраза звучит один раз при
 * появлении, а не зависит от того, заметит ли читалка снятие `aria-hidden`.
 */
export const OfflineBanner = () => {
  const { t } = useLingui();
  const trouble = useOfflineBanner();
  const visible = trouble !== null;
  // Пока полоса сворачивается, текст остаётся прежним, а не пропадает посреди движения.
  const lastTrouble = useRef<ConnectionTrouble>('offline');
  if (trouble) {
    lastTrouble.current = trouble;
  }
  const unreachable = lastTrouble.current === 'unreachable';
  const message = unreachable
    ? t`Can't reach Teamplay. Retrying`
    : t`No internet connection. Waiting for network`;

  return (
    <Box w="100%">
      <Box
        display="grid"
        gridTemplateRows={visible ? '1fr' : '0fr'}
        transitionProperty="grid-template-rows"
        transitionDuration="moderate"
        transitionTimingFunction="ease-in-smooth"
        aria-hidden
        data-testid="offline-banner"
        data-state={visible ? 'open' : 'closed'}
        data-trouble={trouble ?? undefined}
      >
        <Box overflow="hidden" minH={0}>
          <Notice tone="warning" size="bar" icon={unreachable ? ServerOffIcon : WifiOffIcon}>
            {message}
          </Notice>
        </Box>
      </Box>
      <Box role="status" srOnly>
        {visible ? message : null}
      </Box>
    </Box>
  );
};
